import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { PenLine } from "lucide-react";

const STATUS_STYLE = {
  draft: "badge-draft", returned: "badge-nosend", submitted: "badge-neutral",
  reapprove: "badge-nosend", approved: "badge-approved", oninvoice: "badge-neutral", invoiced: "badge-approved",
};
const STATUS_LABEL = {
  draft: "Draft", returned: "Returned", submitted: "Submitted", reapprove: "Needs re-approval",
  approved: "Approved for billing", oninvoice: "On invoice draft", invoiced: "Invoiced",
};
const FILTERS = ["", "draft", "submitted", "returned", "reapprove", "approved", "invoiced"];

export function VisitStatusChip({ status }) {
  return <span className={STATUS_STYLE[status] || "badge-neutral"} data-testid={`visit-status-${status}`}>{STATUS_LABEL[status] || status}</span>;
}

export default function Logs() {
  const [visits, setVisits] = useState([]);
  const [legacy, setLegacy] = useState([]);
  const [filter, setFilter] = useState("");
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setLoaded(false);
    api.get(`/visits${filter ? `?status=${filter}` : ""}`)
      .then((r) => { setVisits(r.data); setLoaded(true); })
      .catch((e) => { toast.error(apiError(e)); setLoaded(true); });
    api.get("/logs").then((r) => setLegacy(r.data)).catch(() => {});
  }, [filter]);

  const fmtElapsed = (m) => (m == null ? "—" : `${Math.floor(m / 60)}h ${m % 60}m`);

  return (
    <div className="max-w-6xl space-y-6" data-testid="logs-page">
      <div className="flex flex-wrap items-end justify-between gap-4 fade-up">
        <div>
          <p className="eyebrow">CKingEducation Daily Visit Digital Log</p>
          <h1 className="section-title text-3xl mt-1">Daily Visit Logs</h1>
        </div>
        <Link to="/visit-work" className="btn-primary" data-testid="new-visit-log-button"><PenLine size={16} /> New Daily Visit Log</Link>
      </div>

      <div className="flex flex-wrap gap-2 fade-up-1" data-testid="visit-status-filters">
        {FILTERS.map((f) => (
          <button key={f || "all"} onClick={() => setFilter(f)} data-testid={`filter-${f || "all"}`}
            className={`rounded-full px-4 py-1.5 text-xs font-bold border transition-colors ${
              filter === f ? "bg-navy text-white border-navy" : "bg-white text-navy border-[#E7E1D4] hover:border-navy"
            }`}>{f ? (STATUS_LABEL[f] || f) : "All"}</button>
        ))}
      </div>

      <div className="card overflow-hidden fade-up-2" data-testid="visits-table-card">
        <table className="w-full">
          <thead className="bg-sand/60">
            <tr>
              <th className="table-th">Date</th><th className="table-th">School</th>
              <th className="table-th">Consultant</th><th className="table-th">Time on site</th>
              <th className="table-th">Activities</th><th className="table-th">Status</th>
            </tr>
          </thead>
          <tbody>
            {visits.map((v) => (
              <tr key={v.id} className="hover:bg-canvas transition-colors cursor-pointer" data-testid={`visit-row-${v.id}`}
                onClick={() => navigate(`/visits/${v.id}`)}>
                <td className="table-td font-bold">{v.date}</td>
                <td className="table-td">{v.school_name || "—"}</td>
                <td className="table-td">{v.consultant_name}</td>
                <td className="table-td text-[#78716C]">{fmtElapsed(v.elapsed_minutes)}</td>
                <td className="table-td text-[#78716C]">{v.activities_count || "—"}</td>
                <td className="table-td"><VisitStatusChip status={v.status} />
                  {v.invoice_number && <span className="text-xs text-[#78716C] ml-1">{v.invoice_number}</span>}</td>
              </tr>
            ))}
            {loaded && visits.length === 0 && (
              <tr><td colSpan={6} className="table-td text-center text-[#78716C] py-10" data-testid="visits-empty">
                No visit logs yet. Start one with <strong>New Daily Visit Log</strong>.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {legacy.length > 0 && (
        <details className="card p-5 fade-up-3" data-testid="legacy-logs-section">
          <summary className="text-sm font-bold text-navy cursor-pointer">Earlier service records (previous format) — {legacy.length}</summary>
          <table className="w-full mt-4">
            <tbody>
              {legacy.map((lg) => (
                <tr key={lg.id} className="hover:bg-canvas transition-colors" data-testid={`legacy-log-row-${lg.id}`}>
                  <td className="table-td font-bold"><Link to={`/logs/${lg.id}`} className="hover:text-terracotta">{lg.date}</Link></td>
                  <td className="table-td">{lg.school_name}</td>
                  <td className="table-td">{lg.consultant_name}</td>
                  <td className="table-td"><span className="badge-neutral">{lg.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}
